import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import SkeletonBlock from '../../components/atoms/Skeleton';

// A 7-day window carries ~2,000 TempEvents — roughly 98% of the timeline. As rows
// they bury every other beat; as one line they answer the question people actually
// bring to them ("was it cold the whole time"). Class names are prefixed so the
// parent page's selectors can't cascade in.
// Shared with the skeleton below rather than copied: the two only reserve the same
// height if they carry the same box, and a skeleton that doesn't is worse than none.
const CARD_CHROME = {
  background: theme.colors.bgCard,
  border: theme.borders.card,
  borderRadius: theme.radii.card,
  padding: theme.spacing.bodyPadding,
  marginBottom: theme.spacing.fieldGap,
};
// Pinned so the head row is a known height on both sides, not whatever the text
// happens to measure.
const HEAD_H = '20px';
const PLOT_H = 130;

const StyledDiv = styledComponent('div', {
  ...CARD_CHROME,

  // Title and readouts share one row. The chart is the content here; the numbers
  // are its caption, so they sit at caption weight rather than as stat tiles that
  // outweigh the line they describe.
  '& .ftc-head': {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing.gapLg,
    minHeight: HEAD_H,
    marginBottom: theme.spacing.gapMd,
  },
  // The caret is the affordance for the whole heading, so it is sized as an icon
  // rather than inheriting the label's 13px, which read as a stray tick mark.
  '& .ftc-caret': {
    fontSize: theme.sizes.iconLg,
    lineHeight: theme.lineHeights.tight,
  },
  '& .ftc-title': {
    ...theme.typography.label,
    color: theme.colors.textDark,
    fontWeight: theme.fontWeights.semibold,
  },
  // A real button so it is focusable and announces its state; the chrome is reset
  // because it has to read as the heading it sits on, not as a control beside it.
  '& .ftc-toggle': {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing.gapSm,
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    cursor: 'pointer',
    color: theme.colors.textMuted,
    '&:focus-visible': {
      outline: `2px solid ${theme.colors.infoBlue}`,
      outlineOffset: 2,
    },
  },
  '& .ftc-stats': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: theme.spacing.gapXl,
  },
  '& .ftc-stat-label': {
    ...theme.typography.hint,
    color: theme.colors.textMuted,
    marginRight: theme.spacing.gapSm,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  '& .ftc-stat-value': {
    ...theme.typography.sectionHeader,
    color: theme.colors.textDark,
  },
  '& .ftc-note': {
    ...theme.typography.hint,
    color: theme.colors.textMuted,
    marginTop: theme.spacing.gapMd,
    marginBottom: 0,
  },
  '& .ftc-tip': {
    background: theme.colors.bgCard,
    border: theme.borders.card,
    borderRadius: theme.radii.badge,
    padding: theme.spacing.smallButtonPadding,
    ...theme.typography.hint,
    color: theme.colors.textBody,
  },
  '& .ftc-tip-temp': {
    fontWeight: theme.fontWeights.semibold,
  },
});

const fmtTick = (sec) => moment.unix(sec).format('MMM D, h:mm A');

function TempTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload;
  return (
    <div className="ftc-tip">
      <div>{fmtTick(p.tsSec)}</div>
      <div className="ftc-tip-temp">{`${p.temp}°`}</div>
    </div>
  );
}

TempTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
};

TempTooltip.defaultProps = {
  active: false,
  payload: null,
};

/**
 * Parse the health snapshot's setpoint, which arrives as a string that may carry a
 * unit suffix ("0.5C"). The readings carry no unit at all, so a setpoint that
 * names one cannot be placed on the same axis without assuming what the readings
 * are — 0.5 drawn on a Fahrenheit axis simply clips out of view and says nothing.
 * @returns {{value: ?number, unit: ?string, text: ?string}}
 */
function readSetpoint(raw) {
  if (raw == null || raw === '') return { value: null, unit: null, text: null };
  const str = String(raw).trim();
  const value = Number.parseFloat(str);
  if (!Number.isFinite(value)) return { value: null, unit: null, text: null };
  const match = str.match(/([CF])\s*$/i);
  const unit = match ? match[1].toUpperCase() : null;
  return { value, unit, text: `${value}°${unit || ''}` };
}

/**
 * Shape the series for recharts, keeping count of the samples that carried no
 * reading — a dropped one is reported, never silently absent. The value is parsed
 * upstream now, so nothing here needs to know how a fridge event stores a
 * temperature.
 */
function toPlot(samples) {
  const points = samples
    .filter((s) => s.value != null)
    .map((s) => ({ tsSec: s.ts, temp: s.value }))
    .sort((a, b) => a.tsSec - b.tsSec);
  return { points, unreadable: samples.length - points.length };
}

export default function TempChart({ samples, setpointRaw, open, onToggle }) {
  const { points, unreadable } = useMemo(() => toPlot(samples), [samples]);
  const setpoint = useMemo(() => readSetpoint(setpointRaw), [setpointRaw]);
  // Only a unitless setpoint shares the readings' scale, so only that one is drawn.
  const plotSetpoint = setpoint.value != null && setpoint.unit === null;

  const stats = useMemo(() => {
    if (!points.length) return null;
    const temps = points.map((p) => p.temp);
    return {
      current: points[points.length - 1].temp,
      min: Math.min(...temps),
      max: Math.max(...temps),
    };
  }, [points]);

  // Computed rather than left to 'dataMin - 2': a reference line outside the domain
  // is dropped without a word, which is the failure this whole branch exists to stop.
  const yDomain = stats
    ? [
        Math.floor(
          Math.min(stats.min, plotSetpoint ? setpoint.value : stats.min) - 2,
        ),
        Math.ceil(
          Math.max(stats.max, plotSetpoint ? setpoint.value : stats.max) + 2,
        ),
      ]
    : [0, 1];

  if (!samples.length) return null;

  return (
    <StyledDiv>
      {stats ? (
        <>
          <div className="ftc-head">
            {/* The control sits on the thing it collapses. Collapsing hides the
                plot and keeps the readings: the summary is the part you still
                want once you have made room for the rows. */}
            <button
              type="button"
              className="ftc-toggle"
              onClick={onToggle}
              aria-expanded={open}
            >
              <span className="ftc-caret" aria-hidden="true">
                {open ? '\u25be' : '\u25b8'}
              </span>
              <span className="ftc-title">Temp Events</span>
            </button>
            {/* The axis already says when the latest reading was, so it isn't
                repeated here. The charted count carries its own denominator, which
                is also what retires a separate "N not plotted" line. */}
            <span className="ftc-stats">
              <span>
                <span className="ftc-stat-label">Latest</span>
                <span className="ftc-stat-value">{`${stats.current}°`}</span>
              </span>
              <span>
                <span className="ftc-stat-label">Low</span>
                <span className="ftc-stat-value">{`${stats.min}°`}</span>
              </span>
              <span>
                <span className="ftc-stat-label">High</span>
                <span className="ftc-stat-value">{`${stats.max}°`}</span>
              </span>
              <span>
                <span className="ftc-stat-label">Charted</span>
                <span className="ftc-stat-value">
                  {unreadable > 0
                    ? `${points.length.toLocaleString()} of ${samples.length.toLocaleString()}`
                    : points.length.toLocaleString()}
                </span>
              </span>
            </span>
          </div>
          {open && (
            <ResponsiveContainer width="100%" height={PLOT_H}>
              <LineChart
                data={points}
                margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                {/* A real time axis, not evenly spaced categories: a fridge that
                  stopped reporting for six hours has to read as a six-hour gap. */}
                <XAxis
                  dataKey="tsSec"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(v) => moment.unix(v).format('MMM D, h:mm A')}
                  minTickGap={64}
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  tickFormatter={(v) => `${v}°`}
                  domain={yDomain}
                  allowDataOverflow={false}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<TempTooltip />} />
                {plotSetpoint && (
                  <ReferenceLine
                    y={setpoint.value}
                    stroke={theme.colors.textHint}
                    strokeDasharray="4 4"
                    label={{
                      value: `setpoint ${setpoint.value}°`,
                      position: 'insideTopRight',
                      fontSize: 11,
                      fill: theme.colors.textMuted,
                    }}
                  />
                )}
                {/* One series, so no legend — the card title names it. Blue, not a
                  status color: a reading is not a verdict on the fridge. */}
                <Line
                  type="monotone"
                  dataKey="temp"
                  stroke={theme.colors.infoBlue}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </>
      ) : (
        <>
          <div className="ftc-head">
            <span className="ftc-title">Temp Events</span>
          </div>
          <p className="ftc-note">
            {`${samples.length.toLocaleString()} temperature readings in range, none with a readable value.`}
          </p>
        </>
      )}
      {open && setpoint.value != null && !plotSetpoint && (
        <p className="ftc-note">{`Setpoint ${setpoint.text} not drawn`}</p>
      )}
    </StyledDiv>
  );
}

const StyledSkeleton = styledComponent('div', {
  ...CARD_CHROME,

  '& .ftc-sk-head': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.gapLg,
    minHeight: HEAD_H,
    marginBottom: theme.spacing.gapMd,
  },
});

/**
 * Same box, same head height, same plot height — so the chart appearing doesn't
 * shove the timeline down the page. The optional setpoint note is NOT reserved:
 * whether it renders depends on data this component doesn't have yet, and a line
 * of blank space on every load is a worse trade than one line of settle.
 */
export function TempChartSkeleton() {
  return (
    <StyledSkeleton aria-hidden>
      <div className="ftc-sk-head">
        <SkeletonBlock width={90} />
        <SkeletonBlock width={220} />
      </div>
      <SkeletonBlock height={PLOT_H} radius={theme.radii.inner} tone="soft" />
    </StyledSkeleton>
  );
}

TempChart.propTypes = {
  // The temperature series, already scoped to the visible window. `value` is null
  // for a sample that carried no reading — kept rather than dropped so the charted
  // count can name its denominator.
  samples: PropTypes.arrayOf(
    PropTypes.shape({
      ts: PropTypes.number.isRequired,
      value: PropTypes.number,
    }),
  ).isRequired,
  // Raw from the health snapshot: a number, or a string that may carry a unit
  // suffix ("0.5C"). Parsed here rather than by the caller, because whether it can
  // be drawn at all depends on the readings this component holds.
  setpointRaw: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

TempChart.defaultProps = {
  setpointRaw: null,
  open: true,
};
